<template>
  <Teleport to="body">
    <Transition name="confirm-fade">
      <div
        v-if="state.visible"
        class="confirm-backdrop"
        role="presentation"
        @click.self="cancel"
      >
        <div
          class="confirm-dialog"
          :class="`confirm-${state.variant}`"
          role="alertdialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="messageId"
          @keydown.esc.prevent="cancel"
          tabindex="-1"
          ref="dialogRef"
        >
          <div class="confirm-icon" aria-hidden="true">
            <svg v-if="state.variant === 'danger'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else-if="state.variant === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4m0 4h.01" stroke-linecap="round"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4m0-4h.01" stroke-linecap="round"/>
            </svg>
          </div>

          <h3 :id="titleId" class="confirm-title">{{ state.title }}</h3>
          <p :id="messageId" class="confirm-message">{{ state.message }}</p>

          <div class="confirm-actions">
            <button type="button" class="confirm-btn confirm-btn-cancel" @click="cancel">
              {{ state.cancelText }}
            </button>
            <button
              type="button"
              class="confirm-btn confirm-btn-confirm"
              :class="`confirm-btn-${state.variant}`"
              ref="confirmBtnRef"
              @click="accept"
            >
              {{ state.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, watch, nextTick, ref } from 'vue';
import { useConfirm } from '@/composables/useConfirm';

export default defineComponent({
  name: 'AppConfirmDialog',
  setup() {
    const { state, accept, cancel } = useConfirm();
    const dialogRef = ref<HTMLElement | null>(null);
    const confirmBtnRef = ref<HTMLButtonElement | null>(null);
    const titleId = 'app-confirm-title';
    const messageId = 'app-confirm-message';

    watch(
      () => state.value.visible,
      async (visible) => {
        if (visible) {
          await nextTick();
          confirmBtnRef.value?.focus();
        }
      }
    );

    return {
      state,
      accept,
      cancel,
      dialogRef,
      confirmBtnRef,
      titleId,
      messageId,
    };
  },
});
</script>

<style scoped>
.confirm-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(26, 26, 26, 0.55);
  backdrop-filter: blur(2px);
}

.confirm-dialog {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem 1.5rem 1.5rem;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(218, 165, 32, 0.12);
  border-top: 4px solid var(--brand-gold, #daa520);
  text-align: center;
  outline: none;
  animation: confirm-pop 0.2s ease-out;
}

.confirm-dialog.confirm-danger {
  border-top-color: #dc2626;
}

.confirm-dialog.confirm-warning {
  border-top-color: var(--brand-gold, #daa520);
}

.confirm-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-icon svg {
  width: 28px;
  height: 28px;
}

.confirm-default .confirm-icon {
  background: rgba(218, 165, 32, 0.15);
  color: var(--brand-gold, #daa520);
}

.confirm-warning .confirm-icon {
  background: rgba(218, 165, 32, 0.18);
  color: #b8860b;
}

.confirm-danger .confirm-icon {
  background: #fee2e2;
  color: #dc2626;
}

.confirm-title {
  margin: 0 0 0.5rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--brand-black, #1a1a1a);
}

.confirm-message {
  margin: 0 0 1.5rem;
  font-size: 0.95rem;
  line-height: 1.55;
  color: #555;
}

.confirm-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

.confirm-btn {
  min-width: 110px;
  padding: 0.65rem 1.25rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  border: none;
}

.confirm-btn:active {
  transform: scale(0.98);
}

.confirm-btn-cancel {
  background: #f3f4f6;
  color: var(--brand-black, #1a1a1a);
  border: 1px solid #e5e7eb;
}

.confirm-btn-cancel:hover {
  background: #e5e7eb;
}

.confirm-btn-confirm.confirm-btn-default {
  background: var(--brand-gold, #daa520);
  color: var(--brand-black, #1a1a1a);
}

.confirm-btn-confirm.confirm-btn-default:hover {
  background: #c4941c;
  box-shadow: 0 4px 12px rgba(218, 165, 32, 0.35);
}

.confirm-btn-confirm.confirm-btn-warning {
  background: var(--brand-gold, #daa520);
  color: var(--brand-black, #1a1a1a);
}

.confirm-btn-confirm.confirm-btn-warning:hover {
  background: #c4941c;
  box-shadow: 0 4px 12px rgba(218, 165, 32, 0.35);
}

.confirm-btn-confirm.confirm-btn-danger {
  background: #dc2626;
  color: #fff;
}

.confirm-btn-confirm.confirm-btn-danger:hover {
  background: #b91c1c;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.35);
}

.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.18s ease;
}

.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}

@keyframes confirm-pop {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 480px) {
  .confirm-dialog {
    padding: 1.5rem 1.25rem 1.25rem;
  }

  .confirm-actions {
    flex-direction: column-reverse;
  }

  .confirm-btn {
    width: 100%;
  }
}
</style>
